import { useEffect, useRef, useState } from 'react';

type Terminal = 'completed' | 'failed';

interface PollState<T> {
  data: T | null;
  error: string | null;
  polling: boolean;
}

/**
 * Polls a job record until it reaches a terminal status.
 * Jobs run for seconds (audit) to minutes (QA), so 2s is a reasonable
 * compromise between a responsive UI and hammering the API.
 */
export function useJobPolling<T extends { status: string }>(
  id: string | null,
  fetcher: (id: string) => Promise<T>,
  intervalMs = 2000,
): PollState<T> {
  const [state, setState] = useState<PollState<T>>({ data: null, error: null, polling: false });
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!id) {
      setState({ data: null, error: null, polling: false });
      return;
    }

    let cancelled = false;
    setState({ data: null, error: null, polling: true });

    const tick = async () => {
      try {
        const data = await fetcher(id);
        if (cancelled) return;
        const done = (['completed', 'failed'] as Terminal[]).includes(data.status as Terminal);
        setState({ data, error: null, polling: !done });
        if (!done) timer.current = window.setTimeout(tick, intervalMs);
      } catch (err) {
        if (cancelled) return;
        setState({ data: null, error: (err as Error).message, polling: false });
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [id, fetcher, intervalMs]);

  return state;
}
